import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Button,
  Checkbox,
  cn,
  DataTable,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogTrigger,
  Flex,
  Heading,
  LoadingOrbit,
  Switch,
  TagBadge,
  Text,
  TextField,
} from '@chatsdet/ui';
import type { ColumnDef, StockFeatures } from '@tanstack/react-table';
import { Copy, Trash, Warning } from '@phosphor-icons/react';
import { API_URL } from '../../../config.js';
import { toApiError } from '../../../lib/api-error.js';
import { copyTextToClipboard } from './clipboard-copy.js';

type ApiKey = {
  id: string;
  name: string;
  keyPrefix: string;
  createdBy: string;
  createdAt: string;
  lastUsedAt: string | null;
  expiresAt: string | null;
  revokedAt: string | null;
  scopes: string[];
  legacyFullAccess: boolean;
  scopedInstanceId: string | null;
};

type ApiKeysSectionProps = {
  isAdmin: boolean;
};

type CreateApiKeyResponse = {
  ok: boolean;
  apiKey: {
    id: string;
    name: string;
    keyPrefix: string;
    createdAt: string;
    expiresAt: string | null;
    scopes: string[];
  };
  rawKey: string;
  error?: string;
};

type ScopeResourceEntry = {
  id: string;
  label: string;
  description: string;
  actions: string[];
};

type ScopeCatalog = {
  resources: ScopeResourceEntry[];
};

function formatRelativeTime(dateString: string | null): string {
  if (!dateString) return 'Never';
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
  if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`;
  return `${Math.floor(diffDays / 365)} years ago`;
}

/**
 * Render a key's effective permissions:
 *  - legacy keys → "Legacy" (orange) — grandfathered surface only
 *  - ['*']        → "Full access" (green)
 *  - empty        → "No access" (red)
 *  - otherwise    → one TagBadge per scope, collapsed beyond a few.
 */
function ScopesCell({ scopes, legacy }: { scopes: string[]; legacy: boolean }) {
  if (legacy) {
    return <TagBadge name="Legacy" color="orange" size="sm" />;
  }
  if (scopes.includes('*')) {
    return <TagBadge name="Full access" color="green" size="sm" />;
  }
  if (scopes.length === 0) {
    return <TagBadge name="No access" color="red" size="sm" />;
  }
  const visible = scopes.slice(0, 3);
  const remaining = scopes.length - visible.length;
  return (
    <Flex align="center" gap="xs" wrap>
      {visible.map((s) => (
        <TagBadge key={s} name={s} color="blue" size="sm" />
      ))}
      {remaining > 0 && <TagBadge name={`+${remaining} more`} color="gray" size="sm" hideDot />}
    </Flex>
  );
}

export function ApiKeysSection({ isAdmin }: ApiKeysSectionProps) {
  const queryClient = useQueryClient();
  const [isCreateOpen, setIsCreateOpen] = React.useState(false);
  const [newKeyName, setNewKeyName] = React.useState('');
  const [fullAccess, setFullAccess] = React.useState(true);
  const [selectedScopes, setSelectedScopes] = React.useState<Set<string>>(new Set());
  const [createdKey, setCreatedKey] = React.useState<string | null>(null);
  const [copySuccess, setCopySuccess] = React.useState(false);
  const [copyError, setCopyError] = React.useState(false);

  const resetCreateForm = React.useCallback(() => {
    setNewKeyName('');
    setFullAccess(true);
    setSelectedScopes(new Set());
  }, []);

  // Fetch API keys
  const keysQuery = useQuery({
    queryKey: ['api-keys'],
    queryFn: async (): Promise<{ ok: boolean; data: ApiKey[] }> => {
      const res = await fetch(`${API_URL}/settings/api-keys`, {
        credentials: 'include',
      });
      if (!res.ok) throw await toApiError(res, 'Failed to fetch API keys');
      return res.json();
    },
    enabled: isAdmin,
  });

  // Fetch scope catalog (server is source of truth)
  const catalogQuery = useQuery({
    queryKey: ['api-keys', 'scopes'],
    queryFn: async (): Promise<{ ok: boolean; data: ScopeCatalog }> => {
      const res = await fetch(`${API_URL}/settings/api-keys/scopes`, {
        credentials: 'include',
      });
      if (!res.ok) throw await toApiError(res, 'Failed to fetch scope catalog');
      return res.json();
    },
    enabled: isAdmin && isCreateOpen,
    staleTime: 60 * 60 * 1000,
  });

  // Create API key
  const createMutation = useMutation({
    mutationFn: async (input: {
      name: string;
      scopes: string[];
    }): Promise<CreateApiKeyResponse> => {
      const res = await fetch(`${API_URL}/settings/api-keys`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(input),
      });
      const data = await res.json();
      if (!res.ok || !data.ok) {
        throw new Error(data.error || 'Failed to create API key');
      }
      return data;
    },
    onSuccess: (data) => {
      setCreatedKey(data.rawKey);
      resetCreateForm();
      setIsCreateOpen(false);
      queryClient.invalidateQueries({ queryKey: ['api-keys'] });
    },
  });

  // Revoke API key
  const revokeMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`${API_URL}/settings/api-keys/${id}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      const data = await res.json();
      if (!res.ok || !data.ok) {
        throw new Error(data.error || 'Failed to revoke API key');
      }
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['api-keys'] });
    },
  });

  const handleCopyKey = async () => {
    if (createdKey) {
      const copied = await copyTextToClipboard(createdKey);
      setCopySuccess(copied);
      setCopyError(!copied);
      setTimeout(() => {
        setCopySuccess(false);
        setCopyError(false);
      }, 2000);
    }
  };

  const handleCloseCreatedKeyDialog = () => {
    setCreatedKey(null);
    setCopySuccess(false);
    setCopyError(false);
  };

  const handleCreate = () => {
    const trimmed = newKeyName.trim();
    if (!trimmed) return;
    const scopes = fullAccess ? ['*'] : Array.from(selectedScopes).sort();
    createMutation.mutate({ name: trimmed, scopes });
  };

  const toggleScope = (scope: string, checked: boolean) => {
    setSelectedScopes((prev) => {
      const next = new Set(prev);
      if (checked) next.add(scope);
      else next.delete(scope);
      return next;
    });
  };

  const toggleResource = (resource: string, actions: readonly string[], on: boolean) => {
    setSelectedScopes((prev) => {
      const next = new Set(prev);
      for (const action of actions) {
        const scope = `${resource}:${action}`;
        if (on) next.add(scope);
        else next.delete(scope);
      }
      return next;
    });
  };

  const activeKeys = (keysQuery.data?.data ?? []).filter((k) => !k.revokedAt);

  const columns = React.useMemo<ColumnDef<StockFeatures, ApiKey>[]>(
    () => [
      {
        accessorKey: 'name',
        header: 'Name',
        cell: ({ row }) => <Text weight="medium">{row.original.name}</Text>,
      },
      {
        accessorKey: 'keyPrefix',
        header: 'Key',
        cell: ({ row }) => (
          <code className="text-xs bg-muted px-2 py-0.5 rounded font-mono">
            {row.original.keyPrefix}...
          </code>
        ),
      },
      {
        id: 'permissions',
        header: 'Permissions',
        cell: ({ row }) => (
          <ScopesCell scopes={row.original.scopes} legacy={row.original.legacyFullAccess} />
        ),
      },
      {
        accessorKey: 'createdAt',
        header: 'Created',
        cell: ({ row }) => (
          <Text size="sm" className="text-muted-foreground">
            {formatRelativeTime(row.original.createdAt)}
          </Text>
        ),
      },
      {
        accessorKey: 'lastUsedAt',
        header: 'Last Used',
        cell: ({ row }) => (
          <Text size="sm" className="text-muted-foreground">
            {formatRelativeTime(row.original.lastUsedAt)}
          </Text>
        ),
      },
      {
        id: 'actions',
        header: '',
        cell: ({ row }) => (
          <Button
            variant="ghost"
            size="sm"
            aria-label="Revoke API key"
            onPress={() => {
              if (
                confirm(
                  'Are you sure you want to revoke this API key? This action cannot be undone.'
                )
              ) {
                revokeMutation.mutate(row.original.id);
              }
            }}
            isDisabled={revokeMutation.isPending}
          >
            <Trash size={16} className="text-destructive" />
          </Button>
        ),
      },
    ],
    [revokeMutation]
  );

  if (!isAdmin) {
    return (
      <Box className="space-y-6">
        <Text className="text-muted-foreground">Only organization admins can manage API keys.</Text>
      </Box>
    );
  }

  const catalog = catalogQuery.data?.data;
  const isCreateDisabled =
    createMutation.isPending || !newKeyName.trim() || (!fullAccess && selectedScopes.size === 0);

  return (
    <>
      <Box className="space-y-6">
        <div className="flex items-start justify-between gap-4">
          <div className="space-y-1">
            <Heading level={2}>API Keys</Heading>
            <Text size="sm" className="text-muted-foreground">
              Create API keys for CI/CD integrations and automation
            </Text>
          </div>
          <Button
            variant="primary"
            onPress={() => {
              resetCreateForm();
              setIsCreateOpen(true);
            }}
          >
            Create Key
          </Button>
        </div>

        {keysQuery.isLoading ? (
          <Flex align="center" justify="center" className="py-12">
            <LoadingOrbit size="md" />
          </Flex>
        ) : activeKeys.length > 0 ? (
          <DataTable columns={columns} data={activeKeys} aria-label="API keys table" />
        ) : (
          <div className="text-center py-12 border-2 border-dashed border-border rounded-lg">
            <Text className="text-muted-foreground">No API keys yet</Text>
            <Text size="sm" className="text-muted-foreground mt-1">
              Create an API key to integrate with your CI/CD pipeline
            </Text>
          </div>
        )}
      </Box>

      {/* Create Key Dialog */}
      <DialogTrigger size="2xl" isOpen={isCreateOpen} onOpenChange={setIsCreateOpen}>
        <span />
        <Dialog title="Create API Key">
          <DialogContent>
            <Box className="space-y-5">
              <TextField
                label="Key Name"
                placeholder="e.g., GitHub Actions, CircleCI"
                value={newKeyName}
                onChange={setNewKeyName}
                isDisabled={createMutation.isPending}
                autoFocus
              />

              {/* Full access — primary toggle, framed as a card so it reads
                  as the headline option above the scoped picker. */}
              <Flex
                align="center"
                justify="between"
                gap="md"
                className={cn(
                  'rounded-lg border p-4 transition-colors shadow-sm',
                  fullAccess
                    ? 'border-primary/40 bg-primary/10'
                    : 'border-border bg-white dark:bg-background'
                )}
              >
                <div className="space-y-1">
                  <Text weight="medium">Full access</Text>
                  <Text size="sm" className="text-muted-foreground">
                    Grants every permission. Equivalent to a session admin.
                  </Text>
                </div>
                <Switch
                  isSelected={fullAccess}
                  onChange={setFullAccess}
                  isDisabled={createMutation.isPending}
                  aria-label="Full access"
                />
              </Flex>

              {!fullAccess && catalog && (
                <ScopedPermissionsPicker
                  catalog={catalog}
                  selected={selectedScopes}
                  onToggle={toggleScope}
                  onToggleResource={toggleResource}
                  onSelectAll={() => {
                    const next = new Set<string>();
                    for (const r of catalog.resources) {
                      for (const a of r.actions) next.add(`${r.id}:${a}`);
                    }
                    setSelectedScopes(next);
                  }}
                  onClearAll={() => setSelectedScopes(new Set())}
                  isDisabled={createMutation.isPending}
                />
              )}
              {!fullAccess && (catalogQuery.isLoading || !catalog) && (
                <Flex align="center" justify="center" className="py-6">
                  <LoadingOrbit size="sm" />
                </Flex>
              )}

              {createMutation.isError && (
                <Text size="sm" className="text-destructive">
                  {(createMutation.error as Error).message}
                </Text>
              )}
            </Box>
          </DialogContent>
          <DialogFooter>
            <Button variant="outline" onPress={() => setIsCreateOpen(false)}>
              Cancel
            </Button>
            <Button variant="primary" onPress={handleCreate} isDisabled={isCreateDisabled}>
              {createMutation.isPending ? 'Creating...' : 'Create Key'}
            </Button>
          </DialogFooter>
        </Dialog>
      </DialogTrigger>

      {/* Show Created Key Dialog */}
      <DialogTrigger isOpen={!!createdKey} onOpenChange={handleCloseCreatedKeyDialog}>
        <span />
        <Dialog title="API Key Created">
          <DialogContent>
            <Box className="space-y-4">
              <Flex
                align="center"
                gap="sm"
                className="p-3 bg-amber-100 dark:bg-amber-900/30 rounded-lg border border-amber-300 dark:border-amber-700"
              >
                <Warning size={20} className="text-amber-600 dark:text-amber-400 flex-shrink-0" />
                <Text size="sm" className="text-amber-800 dark:text-amber-200">
                  Copy this key now - it will not be shown again!
                </Text>
              </Flex>
              <div className="relative">
                <code className="block p-4 bg-muted rounded-lg font-mono text-sm break-all pr-12">
                  {createdKey}
                </code>
                <Button
                  variant="ghost"
                  size="sm"
                  aria-label="Copy API key"
                  className="absolute top-2 right-2"
                  onPress={handleCopyKey}
                >
                  <Copy size={16} />
                </Button>
              </div>
              {copySuccess && (
                <Text size="sm" className="text-success">
                  Copied to clipboard!
                </Text>
              )}
              {copyError && (
                <Text size="sm" className="text-destructive">
                  Could not copy to clipboard. Select the key and copy it manually.
                </Text>
              )}
              <Text size="sm" className="text-muted-foreground">
                Use this key as the <code className="bg-muted px-1 rounded">CANARY_API_TOKEN</code>{' '}
                environment variable in your CI/CD pipeline.
              </Text>
            </Box>
          </DialogContent>
          <DialogFooter>
            <Button variant="primary" onPress={handleCloseCreatedKeyDialog}>
              Done
            </Button>
          </DialogFooter>
        </Dialog>
      </DialogTrigger>
    </>
  );
}

/**
 * Scoped permissions picker — one rounded container with internal dividers,
 * a sticky-ish header showing selection counts plus Select all / Clear, and
 * tidy per-resource rows whose action grids align across resources.
 */
function ScopedPermissionsPicker({
  catalog,
  selected,
  onToggle,
  onToggleResource,
  onSelectAll,
  onClearAll,
  isDisabled,
}: {
  catalog: ScopeCatalog;
  selected: Set<string>;
  onToggle: (scope: string, checked: boolean) => void;
  onToggleResource: (resource: string, actions: readonly string[], on: boolean) => void;
  onSelectAll: () => void;
  onClearAll: () => void;
  isDisabled: boolean;
}) {
  const total = catalog.resources.reduce((n, r) => n + r.actions.length, 0);

  return (
    <div className="space-y-3">
      <Flex align="end" justify="between" gap="md">
        <div className="space-y-1">
          <Text weight="medium">Scoped permissions</Text>
          <Text size="sm" className="text-muted-foreground">
            Pick the actions this key may perform. Anything unchecked is denied.
          </Text>
        </div>
        <Flex align="center" gap="sm">
          <Text size="sm" className="text-muted-foreground tabular-nums whitespace-nowrap">
            {selected.size} / {total} selected
          </Text>
          <Button
            variant="ghost"
            size="sm"
            onPress={onSelectAll}
            isDisabled={isDisabled || selected.size === total}
          >
            Select all
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onPress={onClearAll}
            isDisabled={isDisabled || selected.size === 0}
          >
            Clear
          </Button>
        </Flex>
      </Flex>

      <div className="overflow-hidden rounded-lg border border-border bg-white dark:bg-background divide-y divide-border shadow-sm">
        {catalog.resources.map((resource) => (
          <ResourceScopeRow
            key={resource.id}
            resource={resource}
            selected={selected}
            onToggle={onToggle}
            onToggleResource={onToggleResource}
            isDisabled={isDisabled}
          />
        ))}
      </div>
    </div>
  );
}

/**
 * One row of the scope picker.
 *
 * Header: resource label, count badge ("3 / 8"), master Switch.
 * Body:   per-action Checkboxes laid out in a fixed grid so columns line up
 *         across every resource — much tidier than wrap.
 *
 * The master Switch is `on` only when every action for the resource is
 * selected. Toggling it on selects all; off clears all.
 */
function ResourceScopeRow({
  resource,
  selected,
  onToggle,
  onToggleResource,
  isDisabled,
}: {
  resource: ScopeResourceEntry;
  selected: Set<string>;
  onToggle: (scope: string, checked: boolean) => void;
  onToggleResource: (resource: string, actions: readonly string[], on: boolean) => void;
  isDisabled: boolean;
}) {
  const { id, label, description, actions } = resource;
  const selectedCount = actions.reduce((n, a) => (selected.has(`${id}:${a}`) ? n + 1 : n), 0);
  const allSelected = selectedCount === actions.length;
  const noneSelected = selectedCount === 0;

  return (
    <div
      className={cn(
        'px-4 py-3 transition-colors',
        allSelected && 'bg-primary/10',
        !allSelected && !noneSelected && 'bg-amber-100/60 dark:bg-amber-900/10'
      )}
    >
      <Flex align="start" justify="between" gap="md" className="mb-2">
        <div className="min-w-0 space-y-0.5">
          <Flex align="center" gap="sm">
            <Text weight="medium">{label}</Text>
            <Text
              size="sm"
              className={cn(
                'tabular-nums',
                noneSelected ? 'text-muted-foreground' : 'text-foreground'
              )}
            >
              {selectedCount}/{actions.length}
            </Text>
          </Flex>
          {description && (
            <Text size="sm" className="text-muted-foreground">
              {description}
            </Text>
          )}
        </div>
        <Switch
          size="sm"
          aria-label={`Grant all ${label} permissions`}
          isSelected={allSelected}
          onChange={(on) => onToggleResource(id, actions, on)}
          isDisabled={isDisabled}
        />
      </Flex>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-1.5">
        {actions.map((action) => {
          const scope = `${id}:${action}`;
          return (
            <Checkbox
              key={scope}
              label={<Text size="sm">{action}</Text>}
              isSelected={selected.has(scope)}
              onChange={(checked) => onToggle(scope, checked)}
              isDisabled={isDisabled}
            />
          );
        })}
      </div>
    </div>
  );
}
